import React from 'react';
import { Link } from 'react-scroll';
import { HiArrowRight } from 'react-icons/hi';
import { FaLinkedinIn, FaEnvelope, FaFacebookF } from 'react-icons/fa';

const Hero = () => {
  return (
    <section
      id="home"
      className="min-h-screen flex items-center relative overflow-hidden pt-16 md:pt-20"
    >
      {/* Background Decorative Elements */}
      <div className="absolute top-20 right-0 w-72 h-72 bg-soft-pink rounded-full blur-3xl opacity-60" />
      <div className="absolute bottom-20 left-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl opacity-40" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full relative z-10 py-8 md:py-0">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left Content */}
          <div className="order-2 md:order-1">
            <div className="inline-flex items-center gap-2 bg-soft-pink/60 px-4 py-2 rounded-full mb-6 animate-fadeInUp">
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <span className="text-primary-dark text-sm font-medium">
                Based in the United Kingdom
              </span>
            </div>

            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-text-dark leading-tight mb-4 animate-slideInLeft [animation-delay:0.2s] opacity-0 [animation-fill-mode:forwards]">
              Hi, I'm{' '}
              <span className="text-primary inline-block animate-scaleIn [animation-delay:0.6s] opacity-0 [animation-fill-mode:forwards]">
                Marina Naznin
              </span>
            </h1>

            <h2 className="text-lg md:text-xl text-primary-light font-medium mb-4 animate-fadeInUp [animation-delay:0.8s] opacity-0 [animation-fill-mode:forwards]">
              Career & Professional Services Specialist
            </h2>

            <p className="text-text-light text-base md:text-lg leading-relaxed mb-8 max-w-lg animate-fadeInUp [animation-delay:1s] opacity-0 [animation-fill-mode:forwards]">
              Helping you build your future with confidence. From crafting the
              perfect CV to navigating student support, I provide personalised
              guidance every step of the way.
            </p>

            <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-8 md:mb-10">
              <Link
                to="contact"
                smooth={true}
                offset={-80}
                duration={500}
                className="group inline-flex items-center gap-2 bg-primary text-white px-6 py-3 md:px-7 md:py-3.5 rounded-full text-sm md:text-base font-medium hover:bg-primary-dark transition-all duration-300 cursor-pointer hover:shadow-lg hover:shadow-primary/25"
              >
                Get Started
                <HiArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="services"
                smooth={true}
                offset={-80}
                duration={500}
                className="inline-flex items-center gap-2 border-2 border-primary/30 text-primary-dark px-6 py-3 md:px-7 md:py-3.5 rounded-full text-sm md:text-base font-medium hover:border-primary hover:bg-soft-pink/30 transition-all duration-300 cursor-pointer"
              >
                View Services
              </Link>
            </div>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-text-light">Follow me:</span>
              <a
                href="https://www.linkedin.com/in/marina-naznin-217555391/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-warm-gray flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300"
                aria-label="LinkedIn"
              >
                <FaLinkedinIn size={16} />
              </a>
              <a
                href="https://www.facebook.com/ashmari.ashmari.3"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-warm-gray flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300"
                aria-label="Facebook"
              >
                <FaFacebookF size={16} />
              </a>
              <a
                href="mailto:marinanaznin.ac@gmail.com"
                className="w-10 h-10 rounded-full bg-warm-gray flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300"
                aria-label="Email"
              >
                <FaEnvelope size={16} />
              </a>
            </div>
          </div>

          {/* Right - Illustration/Avatar Area */}
          <div className="order-1 md:order-2 flex justify-center mb-8 md:mb-0">
            <div className="relative group/hero">
              <div className="w-56 h-56 md:w-80 md:h-80 lg:w-96 lg:h-96 rounded-full bg-gradient-to-br from-primary-light/30 via-soft-pink to-accent/30 flex items-center justify-center p-2 transition-transform duration-700 ease-out group-hover/hero:scale-105">
                <div className="w-48 h-48 md:w-72 md:h-72 lg:w-80 lg:h-80 rounded-full bg-gradient-to-br from-primary/20 to-accent/40 flex items-center justify-center p-2 overflow-hidden">
                  <img
                    src="/images/marina-profile.png"
                    alt="Marina Naznin"
                    className="w-full h-full object-cover rounded-full transition-transform duration-700 ease-out group-hover/hero:scale-110 group-hover/hero:rotate-2"
                  />
                </div>
              </div>
              {/* Floating Elements */}
              <div className="absolute -top-2 -right-2 md:-top-4 md:-right-4 bg-white rounded-2xl shadow-lg px-3 py-2 md:px-4 md:py-3 animate-bounce">
                <span className="text-xs md:text-sm font-medium text-primary-dark">
                  UK Based
                </span>
              </div>
              <div className="absolute -bottom-1 -left-2 md:-bottom-2 md:-left-4 bg-white rounded-2xl shadow-lg px-3 py-2 md:px-4 md:py-3">
                <span className="text-xs md:text-sm font-medium text-primary-dark">
                  250+ Clients
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
